import type { MembershipRole } from "@safeplate/contracts";
import {
  Badge,
  Banner,
  Button,
  Card,
  CardContent,
  Dialog,
  Disclosure,
  Input,
  Label,
  PageHeader,
  Select,
  Tabs,
} from "@safeplate/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { GraduationCap, Plus, Users, X } from "lucide-react";
import { useId, useState, type FormEvent, type ReactNode } from "react";

import { displayError } from "../api/errors.js";
import { EmptyState, ErrorState, LoadingState } from "../components/states.js";
import { useShell } from "../shell/shell-context.js";
import {
  STAFF_LOGINS_READY,
  changeMembership,
  createStaff,
  createStaffLogins,
  grantMembership,
  inviteStaff,
  listStaff,
  reissueInvitation,
  revokeInvitation,
  setStaffQuickPin,
  updateStaff,
  type Invitation,
  type StaffProfile,
} from "../setup/setup-api.js";
import { initialsOf } from "../quick-access/people-on-computer.js";
import {
  PIN_LENGTH,
  PIN_PATTERN,
  pinInput,
  pinProblem as pinRule,
} from "../quick-access/pin.js";
import { TrainingWorkspacePage } from "../training/training-page.js";
import {
  PASSWORD_MAX,
  addPeople,
  blankPerson,
  combineResults,
  namedPeople,
  readyToAdd,
  rowProblem,
  type AddMode,
  type AddPeopleResult,
  type LoginDetails,
  type NewPerson,
} from "./add-people.js";
import { PersonChecksDialog } from "./person-checks.js";
import { personRow, roleLabel } from "./person-status.js";

const FIELD_ERROR = "mt-4 text-sm font-semibold text-red-800";

function rolesFor(activeRole: MembershipRole): MembershipRole[] {
  return activeRole === "admin" ? ["staff", "manager", "admin"] : ["staff"];
}

function RoleOptions({ roles }: { roles: readonly MembershipRole[] }) {
  return roles.map((role) => (
    <option key={role} value={role}>
      {roleLabel(role)}
    </option>
  ));
}

function LoginDetailsList({ logins }: { logins: readonly LoginDetails[] }) {
  return (
    <ul className="divide-y divide-stone-200 rounded-lg border border-stone-200">
      {logins.map((login) => (
        <li
          className="grid gap-x-4 gap-y-1 p-3 sm:grid-cols-2"
          key={login.email}
        >
          <p className="text-sm font-bold sm:col-span-2">{login.name}</p>
          <p className="text-sm">
            <span className="text-stone-500">Email </span>
            {login.email}
          </p>
          <p className="text-sm">
            <span className="text-stone-500">Password </span>
            <span className="font-mono">{login.password}</span>
          </p>
          {login.pin ? (
            <p className="text-sm">
              <span className="text-stone-500">PIN </span>
              <span className="font-mono">{login.pin}</span>
            </p>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function AddPeopleDialog({
  activeRole,
  locationId,
  onClose,
  onDone,
}: {
  activeRole: MembershipRole;
  locationId: string;
  onClose: () => void;
  onDone: (result: AddPeopleResult) => void;
}) {
  const id = useId();
  const shell = useShell();
  const queryClient = useQueryClient();
  const roles = rolesFor(activeRole);
  const [mode, setMode] = useState<AddMode>(
    STAFF_LOGINS_READY ? "logins" : "invite",
  );
  const [rows, setRows] = useState<NewPerson[]>(() =>
    Array.from({ length: 3 }, () => blankPerson()),
  );
  const [errors, setErrors] = useState<Map<number, string>>(new Map());
  const [selected, setSelected] = useState<string[]>([locationId]);
  // What earlier tries added; logins made here are shown once to hand over.
  const [done, setDone] = useState<AddPeopleResult | null>(null);
  const [handover, setHandover] = useState(false);
  const named = namedPeople(rows);
  const logins = mode === "logins";

  const save = useMutation({
    mutationFn: () =>
      addPeople(rows, selected, mode, {
        createStaff,
        createStaffLogins,
        inviteStaff,
      }),
    onSuccess: async (latest) => {
      await queryClient.invalidateQueries({ queryKey: ["staff", locationId] });
      const result = combineResults(done, latest);
      setDone(result);
      if (result.failed.length === 0) {
        if (result.logins.length > 0) setHandover(true);
        else onDone(result);
        return;
      }
      // Keep only the people who were not added, with what went wrong.
      setRows(result.failed.map((entry) => entry.person));
      setErrors(
        new Map(
          result.failed.flatMap((entry, index) =>
            entry.error === null
              ? []
              : [[index, displayError(entry.error)] as const],
          ),
        ),
      );
    },
  });

  const update = (index: number, change: Partial<NewPerson>) =>
    setRows((current) =>
      current.map((row, position) =>
        position === index ? { ...row, ...change } : row,
      ),
    );

  // Never close while saving, and never lose logins already made: their
  // passwords are shown only here.
  const close = () => {
    if (save.isPending) return;
    if (done && done.logins.length > 0) setHandover(true);
    else if (done) onDone(done);
    else onClose();
  };

  if (handover && done)
    return (
      <Dialog
        footer={<Button onClick={() => onDone(done)}>Done</Button>}
        onClose={() => onDone(done)}
        open
        size="lg"
        title="Login details"
      >
        <LoginDetailsList logins={done.logins} />
      </Dialog>
    );

  // On a phone, name and email take a line each; the rest share one.
  const columns = logins
    ? roles.length > 1
      ? "grid-cols-[1fr_1fr_4.5rem_2.75rem] sm:grid-cols-[1.1fr_1.4fr_6.5rem_1.1fr_4.5rem_2.75rem]"
      : "grid-cols-[1fr_4.5rem_2.75rem] sm:grid-cols-[1.1fr_1.4fr_6.5rem_1.1fr_4.5rem_2.75rem]"
    : "grid-cols-[1fr_1fr_2.75rem] sm:grid-cols-[1.2fr_1.5fr_7rem_6.5rem_2.75rem]";

  return (
    <Dialog
      footer={
        <>
          <Button disabled={save.isPending} onClick={close} variant="ghost">
            Cancel
          </Button>
          <Button
            disabled={
              save.isPending || !readyToAdd(rows, mode) || selected.length === 0
            }
            form={`${id}-form`}
            type="submit"
          >
            {save.isPending
              ? "Adding…"
              : named.length > 1
                ? `Add ${named.length} people`
                : "Add person"}
          </Button>
        </>
      }
      onClose={close}
      open
      size="lg"
      title="Add people"
    >
      <form
        className="space-y-4"
        id={`${id}-form`}
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          setErrors(new Map());
          save.mutate();
        }}
      >
        {done && done.logins.length > 0 ? (
          <Disclosure defaultOpen summary="Login details">
            <LoginDetailsList logins={done.logins} />
          </Disclosure>
        ) : null}
        <div
          className={`hidden gap-3 text-sm font-medium text-stone-800 sm:grid ${columns}`}
        >
          <span>Name</span>
          <span>{logins ? "Email" : "Email (optional)"}</span>
          <span>{roles.length > 1 ? "Role" : ""}</span>
          {logins ? <span>Password</span> : null}
          <span>PIN</span>
        </div>
        <ol className="divide-y divide-stone-200">
          {rows.map((row, index) => {
            const hasEmail = row.email.trim() !== "";
            const problem = rowProblem(row, mode) ?? errors.get(index);
            return (
              <li
                className={`grid gap-2 py-3 first:pt-0 sm:items-start sm:gap-3 sm:py-2 ${columns}`}
                key={index}
              >
                <Input
                  aria-label={`Name ${index + 1}`}
                  autoComplete="off"
                  className="col-span-full sm:col-span-1"
                  onChange={(event) =>
                    update(index, { name: event.target.value })
                  }
                  placeholder="Name"
                  value={row.name}
                />
                <Input
                  aria-label={`Email ${index + 1}`}
                  autoComplete="off"
                  className="col-span-full sm:col-span-1"
                  onChange={(event) =>
                    update(index, { email: event.target.value })
                  }
                  placeholder="Email"
                  type="email"
                  value={row.email}
                />
                {roles.length > 1 ? (
                  <Select
                    aria-label={`Role ${index + 1}`}
                    onChange={(event) =>
                      update(index, {
                        role: event.target.value as MembershipRole,
                      })
                    }
                    value={row.role}
                  >
                    <RoleOptions roles={roles} />
                  </Select>
                ) : (
                  <span className="hidden sm:block" />
                )}
                {logins ? (
                  <Input
                    aria-label={`Password ${index + 1}`}
                    autoComplete="off"
                    disabled={!hasEmail}
                    maxLength={PASSWORD_MAX}
                    onChange={(event) =>
                      update(index, { password: event.target.value })
                    }
                    placeholder="Password"
                    spellCheck={false}
                    value={hasEmail ? row.password : ""}
                  />
                ) : null}
                <Input
                  aria-label={`PIN ${index + 1}`}
                  autoComplete="off"
                  className={
                    roles.length > 1 || logins
                      ? undefined
                      : "col-span-2 sm:col-span-1"
                  }
                  disabled={!hasEmail}
                  inputMode="numeric"
                  maxLength={PIN_LENGTH}
                  onChange={(event) =>
                    update(index, { pin: pinInput(event.target.value) })
                  }
                  placeholder="PIN"
                  type={logins ? "text" : "password"}
                  value={hasEmail ? row.pin : ""}
                />
                <Button
                  aria-label={`Remove row ${index + 1}`}
                  className="justify-self-start"
                  disabled={rows.length === 1}
                  onClick={() => {
                    setRows((current) =>
                      current.filter((_, position) => position !== index),
                    );
                    setErrors(new Map());
                  }}
                  size="icon"
                  variant="ghost"
                >
                  <X aria-hidden="true" className="size-4" />
                </Button>
                {problem ? (
                  <p
                    className="col-span-full text-sm font-semibold text-red-800"
                    role="alert"
                  >
                    {problem}
                  </p>
                ) : null}
              </li>
            );
          })}
        </ol>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button
            onClick={() => setRows((current) => [...current, blankPerson()])}
            size="sm"
            variant="outline"
          >
            <Plus aria-hidden="true" className="size-4" /> Another person
          </Button>
          {STAFF_LOGINS_READY ? (
            <Button
              onClick={() => setMode(logins ? "invite" : "logins")}
              size="sm"
              variant="ghost"
            >
              {logins ? "Invite by email instead" : "Set passwords instead"}
            </Button>
          ) : null}
        </div>
        {shell.locations.length > 1 ? (
          <fieldset>
            <legend className="text-sm font-medium">Locations</legend>
            <div className="mt-2 space-y-1">
              {shell.locations.map((location) => (
                <label
                  className="flex cursor-pointer items-center gap-3 rounded-lg p-2 hover:bg-stone-50"
                  key={location.id}
                >
                  <input
                    checked={selected.includes(location.id)}
                    className="size-4 accent-emerald-800"
                    onChange={(event) =>
                      setSelected((current) =>
                        event.target.checked
                          ? [...current, location.id]
                          : current.filter((item) => item !== location.id),
                      )
                    }
                    type="checkbox"
                  />
                  <span className="text-sm">{location.name}</span>
                </label>
              ))}
            </div>
          </fieldset>
        ) : null}
        {save.error ? (
          <p className={FIELD_ERROR} role="alert">
            {displayError(save.error)}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}

function EditPersonDialog({
  isAdmin,
  item,
  locationId,
  onCancelInvite,
  onChangePin,
  onClose,
  onResend,
}: {
  isAdmin: boolean;
  item: StaffProfile;
  locationId: string;
  onCancelInvite: () => void;
  onChangePin: () => void;
  onClose: () => void;
  onResend: () => void;
}) {
  const id = useId();
  const row = personRow(item);
  const hasMore =
    (row.canSetPin && item.quick_pin_enabled) ||
    row.pendingInvitation ||
    isAdmin;
  const queryClient = useQueryClient();
  const save = useMutation({
    mutationFn: (form: FormData) =>
      updateStaff(item.id, {
        display_name: String(form.get("display_name") ?? "").trim(),
        email: String(form.get("email") ?? "").trim() || null,
        job_title: String(form.get("job_title") ?? "").trim() || null,
        status: form.get("status") === "inactive" ? "inactive" : "active",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["staff", locationId] });
      onClose();
    },
  });

  return (
    <Dialog
      footer={
        <>
          <Button onClick={onClose} variant="ghost">
            Cancel
          </Button>
          <Button disabled={save.isPending} form={`${id}-form`} type="submit">
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </>
      }
      onClose={onClose}
      open
      title={`Edit ${item.display_name}`}
    >
      <form
        className="space-y-4"
        id={`${id}-form`}
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          save.mutate(new FormData(event.currentTarget));
        }}
      >
        <div>
          <Label htmlFor={`${id}-name`}>Name</Label>
          <Input
            className="mt-1.5"
            defaultValue={item.display_name}
            id={`${id}-name`}
            name="display_name"
            required
          />
        </div>
        <div>
          <Label htmlFor={`${id}-email`}>Email</Label>
          <Input
            className="mt-1.5"
            defaultValue={item.email ?? ""}
            id={`${id}-email`}
            name="email"
            type="email"
          />
        </div>
        <div>
          <Label htmlFor={`${id}-job`}>Job title</Label>
          <Input
            className="mt-1.5"
            defaultValue={item.job_title ?? ""}
            id={`${id}-job`}
            name="job_title"
          />
        </div>
        <div>
          <Label htmlFor={`${id}-status`}>Status</Label>
          <Select
            className="mt-1.5"
            defaultValue={item.status}
            id={`${id}-status`}
            name="status"
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </Select>
        </div>
        {hasMore ? (
          <Disclosure summary="More">
            <div className="flex flex-wrap gap-2">
              {row.canSetPin && item.quick_pin_enabled ? (
                <Button onClick={onChangePin} size="sm" variant="outline">
                  Change PIN
                </Button>
              ) : null}
              {row.pendingInvitation ? (
                <>
                  <Button onClick={onResend} size="sm" variant="outline">
                    Resend invite
                  </Button>
                  <Button onClick={onCancelInvite} size="sm" variant="outline">
                    Cancel invite
                  </Button>
                </>
              ) : null}
              {isAdmin ? (
                <Link
                  className="inline-flex h-9 items-center rounded-lg border border-stone-300 bg-surface px-3 text-xs font-semibold text-stone-800 hover:border-emerald-700 hover:text-emerald-900"
                  search={{ staff_profile_id: item.id }}
                  to="/privacy"
                >
                  Privacy
                </Link>
              ) : null}
            </div>
          </Disclosure>
        ) : null}
        {save.error ? (
          <p className={FIELD_ERROR} role="alert">
            {displayError(save.error)}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}

function PinDialog({
  item,
  locationId,
  onClose,
}: {
  item: StaffProfile;
  locationId: string;
  onClose: () => void;
}) {
  const id = useId();
  const queryClient = useQueryClient();
  const [pin, setPin] = useState("");
  const problem = pin.length === PIN_LENGTH ? pinRule(pin) : null;
  const mutation = useMutation({
    mutationFn: (value: string) => setStaffQuickPin(item.id, value),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["staff", locationId] });
      onClose();
    },
  });
  return (
    <Dialog
      footer={
        <>
          <Button onClick={onClose} variant="ghost">
            Cancel
          </Button>
          <Button
            disabled={
              mutation.isPending ||
              pin.length !== PIN_LENGTH ||
              problem !== null
            }
            form={`${id}-form`}
            type="submit"
          >
            {mutation.isPending ? "Saving…" : "Save PIN"}
          </Button>
        </>
      }
      onClose={onClose}
      open
      size="sm"
      title={`${item.quick_pin_enabled ? "Change" : "Set"} PIN for ${item.display_name}`}
    >
      <form
        id={`${id}-form`}
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          mutation.mutate(pin);
        }}
      >
        <Label htmlFor={`${id}-value`}>4-digit PIN</Label>
        <Input
          autoComplete="off"
          className="mt-2 text-center text-xl tracking-[0.3em]"
          id={`${id}-value`}
          inputMode="numeric"
          maxLength={PIN_LENGTH}
          minLength={PIN_LENGTH}
          name="pin"
          onChange={(event) => setPin(pinInput(event.target.value))}
          pattern={PIN_PATTERN}
          required
          type="password"
          value={pin}
        />
        {problem || mutation.error ? (
          <p className={FIELD_ERROR} role="alert">
            {problem ?? displayError(mutation.error)}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}

function InviteDialog({
  activeRole,
  item,
  locationId,
  onClose,
  onSent,
}: {
  activeRole: MembershipRole;
  item: StaffProfile;
  locationId: string;
  onClose: () => void;
  onSent: (invitation: Invitation) => void;
}) {
  const id = useId();
  const send = useMutation({
    mutationFn: (form: FormData) => {
      const role = String(form.get("role") ?? "staff") as MembershipRole;
      return inviteStaff({
        email: String(form.get("email") ?? "").trim(),
        location_ids: role === "admin" ? [] : [locationId],
        role,
        staff_profile_id: item.id,
      });
    },
    onSuccess: onSent,
  });

  return (
    <Dialog
      footer={
        <>
          <Button onClick={onClose} variant="ghost">
            Cancel
          </Button>
          <Button disabled={send.isPending} form={`${id}-form`} type="submit">
            {send.isPending ? "Sending…" : "Send invite"}
          </Button>
        </>
      }
      onClose={onClose}
      open
      title={`Invite ${item.display_name}`}
    >
      <form
        className="space-y-4"
        id={`${id}-form`}
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          send.mutate(new FormData(event.currentTarget));
        }}
      >
        <div>
          <Label htmlFor={`${id}-email`}>Email</Label>
          <Input
            className="mt-1.5"
            defaultValue={item.email ?? ""}
            id={`${id}-email`}
            name="email"
            required
            type="email"
          />
        </div>
        <div>
          <Label htmlFor={`${id}-role`}>Role</Label>
          <Select className="mt-1.5" id={`${id}-role`} name="role">
            <RoleOptions roles={rolesFor(activeRole)} />
          </Select>
        </div>
        {send.error ? (
          <p className={FIELD_ERROR} role="alert">
            {displayError(send.error)}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}

function CancelInvitationDialog({
  invitationId,
  name,
  onClose,
  onDone,
}: {
  invitationId: string;
  name: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const revoke = useMutation({
    mutationFn: () => revokeInvitation(invitationId),
    onSuccess: onDone,
  });
  return (
    <Dialog
      footer={
        <>
          <Button onClick={onClose} variant="ghost">
            Keep
          </Button>
          <Button
            disabled={revoke.isPending}
            onClick={() => revoke.mutate()}
            variant="destructive"
          >
            {revoke.isPending ? "Cancelling…" : "Cancel invite"}
          </Button>
        </>
      }
      onClose={onClose}
      open
      size="sm"
      title={`Cancel ${name}’s invite?`}
    >
      {revoke.error ? (
        <p className="text-sm font-semibold text-red-800" role="alert">
          {displayError(revoke.error)}
        </p>
      ) : null}
    </Dialog>
  );
}

function MembershipRoleControl({
  activeRole,
  locationId,
  person,
}: {
  activeRole: MembershipRole;
  locationId: string;
  person: StaffProfile;
}) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (role: MembershipRole) => {
      const input = {
        location_id: role === "admin" ? null : locationId,
        role,
      };
      return person.membership
        ? changeMembership(person.membership.id, input)
        : grantMembership({ ...input, user_id: person.user_id! });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["staff", locationId] }),
        queryClient.invalidateQueries({ queryKey: ["me"] }),
      ]);
    },
  });
  const roles: MembershipRole[] =
    activeRole === "admin"
      ? ["staff", "manager", "admin"]
      : ["staff", "manager"];
  const currentRole = person.membership?.role;
  const canChange =
    person.user_id !== null &&
    person.status === "active" &&
    !(activeRole === "manager" && currentRole === "admin");

  return (
    <div className="min-w-32">
      <Select
        aria-label={`Role for ${person.display_name}`}
        disabled={!canChange || mutation.isPending}
        onChange={(event) =>
          mutation.mutate(event.target.value as MembershipRole)
        }
        size="sm"
        value={currentRole ?? ""}
      >
        {!currentRole ? <option value="">No access</option> : null}
        {currentRole === "admin" && !roles.includes("admin") ? (
          <option value="admin">Admin</option>
        ) : null}
        <RoleOptions roles={roles} />
      </Select>
      {mutation.error ? (
        <p
          className="mt-1 max-w-52 text-xs font-semibold text-red-800"
          role="alert"
        >
          {displayError(mutation.error)}
        </p>
      ) : null}
    </div>
  );
}

type Notice =
  | { kind: "sent"; email: string }
  | { kind: "not_sent"; name: string }
  | { kind: "added"; name: string }
  | { kind: "batch"; result: AddPeopleResult };

function batchTitle(result: AddPeopleResult): string {
  const people = result.added === 1 ? "1 person" : `${result.added} people`;
  const invites =
    result.invited === 0
      ? ""
      : result.invited === 1
        ? " · 1 invite sent"
        : ` · ${result.invited} invites sent`;
  const missed =
    result.notInvited.length > 0
      ? ` · Not sent to ${result.notInvited.join(", ")}`
      : "";
  return `${people} added${invites}${missed}`;
}

function noticeTitle(notice: Notice): string {
  if (notice.kind === "batch") return batchTitle(notice.result);
  return notice.kind === "sent"
    ? `Invite sent to ${notice.email}`
    : notice.kind === "not_sent"
      ? `${notice.name} added. Invite not sent`
      : `${notice.name} added`;
}

function Toolbar({ actions, tabs }: { actions?: ReactNode; tabs: ReactNode }) {
  if (!tabs && !actions) return null;
  return (
    <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
      <div className="min-w-0">{tabs}</div>
      {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
    </div>
  );
}

function PeopleWorkspacePage({ tabs }: { tabs: ReactNode }) {
  const shell = useShell();
  const queryClient = useQueryClient();
  const locationId = shell.activeLocation?.id;
  const canManage = shell.activeRole !== "staff";
  const isAdmin = shell.activeRole === "admin";
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<StaffProfile | null>(null);
  const [inviting, setInviting] = useState<StaffProfile | null>(null);
  const [settingPin, setSettingPin] = useState<StaffProfile | null>(null);
  const [cancelling, setCancelling] = useState<StaffProfile | null>(null);
  const [checking, setChecking] = useState<StaffProfile | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const staff = useQuery({
    enabled: Boolean(locationId && canManage),
    queryFn: () => listStaff(locationId!),
    queryKey: ["staff", locationId],
  });
  const resend = useMutation({
    mutationFn: (invitationId: string) => reissueInvitation(invitationId),
    onSuccess: async (invitation) => {
      sent(invitation);
      await queryClient.invalidateQueries({ queryKey: ["staff", locationId] });
    },
  });

  function sent(invitation: Invitation) {
    setNotice({ email: invitation.email, kind: "sent" });
  }

  if (!canManage) return null;

  const addButton = locationId ? (
    <Button onClick={() => setAdding(true)}>
      <Plus aria-hidden="true" className="size-4" /> Add people
    </Button>
  ) : null;

  return (
    <>
      <Toolbar actions={addButton} tabs={tabs} />

      {!locationId ? (
        <EmptyState title="Choose a location" />
      ) : (
        <>
          {notice ? (
            <Banner
              className="mb-4"
              title={noticeTitle(notice)}
              tone={
                notice.kind === "not_sent" ||
                (notice.kind === "batch" && notice.result.notInvited.length > 0)
                  ? "attention"
                  : "positive"
              }
            />
          ) : null}
          {resend.error ? (
            <Banner
              className="mb-4"
              title={displayError(resend.error)}
              tone="danger"
            />
          ) : null}
          {staff.isPending ? <LoadingState label="Loading the team" /> : null}
          {staff.error ? (
            <ErrorState
              message={displayError(staff.error)}
              onAction={() => void staff.refetch()}
              title="The team isn’t available"
            />
          ) : null}
          {staff.data?.length === 0 ? (
            <EmptyState
              action={
                <Button onClick={() => setAdding(true)}>
                  <Plus aria-hidden="true" className="size-4" /> Add people
                </Button>
              }
              calm
              title="Add your first person"
            />
          ) : null}
          {staff.data && staff.data.length > 0 ? (
            <Card>
              <CardContent className="divide-y divide-stone-200 p-0">
                {staff.data.map((person) => {
                  const row = personRow(person);
                  return (
                    <div
                      className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center"
                      key={person.id}
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-3">
                        <span
                          aria-hidden="true"
                          className="grid size-10 shrink-0 place-items-center rounded-full bg-stone-100 text-xs font-black text-stone-700"
                        >
                          {initialsOf(person.display_name)}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            <p className="min-w-0 truncate text-sm font-bold">
                              {person.display_name}
                            </p>
                            {row.chip ? (
                              <Badge tone={row.chip.tone}>
                                {row.chip.label}
                              </Badge>
                            ) : null}
                          </div>
                          {person.job_title || person.email ? (
                            <p className="mt-1 truncate text-xs text-stone-500">
                              {[person.job_title, person.email]
                                .filter(Boolean)
                                .join(" · ")}
                            </p>
                          ) : null}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-start gap-1 pl-13 sm:pl-0">
                        {person.user_id ? (
                          <MembershipRoleControl
                            activeRole={shell.activeRole}
                            locationId={locationId}
                            person={person}
                          />
                        ) : null}
                        {row.invite && person.status === "active" ? (
                          <Button
                            disabled={resend.isPending}
                            onClick={() =>
                              row.invite === "invite"
                                ? setInviting(person)
                                : person.invitation
                                  ? resend.mutate(person.invitation.id)
                                  : setInviting(person)
                            }
                            size="sm"
                            variant="outline"
                          >
                            {row.invite === "invite" ? "Invite" : "Send again"}
                          </Button>
                        ) : null}
                        {row.showSetPin ? (
                          <Button
                            onClick={() => setSettingPin(person)}
                            size="sm"
                            variant="ghost"
                          >
                            Set PIN
                          </Button>
                        ) : null}
                        {person.status === "active" ? (
                          <Button
                            aria-label={`${person.display_name}’s checks`}
                            onClick={() => setChecking(person)}
                            size="sm"
                            variant="ghost"
                          >
                            Checks
                          </Button>
                        ) : null}
                        <Button
                          aria-label={`Edit ${person.display_name}`}
                          onClick={() => setEditing(person)}
                          size="sm"
                          variant="ghost"
                        >
                          Edit
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          ) : null}
        </>
      )}

      {adding && locationId ? (
        <AddPeopleDialog
          activeRole={shell.activeRole}
          locationId={locationId}
          onClose={() => setAdding(false)}
          onDone={(result) => {
            setNotice({ kind: "batch", result });
            setAdding(false);
          }}
        />
      ) : null}
      {editing && locationId ? (
        <EditPersonDialog
          isAdmin={isAdmin}
          item={editing}
          locationId={locationId}
          onCancelInvite={() => {
            setCancelling(editing);
            setEditing(null);
          }}
          onChangePin={() => {
            setSettingPin(editing);
            setEditing(null);
          }}
          onClose={() => setEditing(null)}
          onResend={() => {
            if (editing.invitation) resend.mutate(editing.invitation.id);
            setEditing(null);
          }}
        />
      ) : null}
      {inviting && locationId ? (
        <InviteDialog
          activeRole={shell.activeRole}
          item={inviting}
          locationId={locationId}
          onClose={() => setInviting(null)}
          onSent={async (invitation) => {
            sent(invitation);
            setInviting(null);
            await queryClient.invalidateQueries({
              queryKey: ["staff", locationId],
            });
          }}
        />
      ) : null}
      {checking && locationId ? (
        <PersonChecksDialog
          locationId={locationId}
          onClose={() => setChecking(null)}
          person={checking}
        />
      ) : null}
      {settingPin && locationId ? (
        <PinDialog
          item={settingPin}
          locationId={locationId}
          onClose={() => setSettingPin(null)}
        />
      ) : null}
      {cancelling?.invitation && locationId ? (
        <CancelInvitationDialog
          invitationId={cancelling.invitation.id}
          name={cancelling.display_name}
          onClose={() => setCancelling(null)}
          onDone={async () => {
            setNotice(null);
            setCancelling(null);
            await queryClient.invalidateQueries({
              queryKey: ["staff", locationId],
            });
          }}
        />
      ) : null}
    </>
  );
}

type TeamView = "people" | "training";

export function TeamWorkspacePage() {
  const shell = useShell();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { tab?: unknown };
  const canManage = shell.activeRole !== "staff";
  const view: TeamView =
    canManage && search.tab !== "training" ? "people" : "training";

  const tabs = canManage ? (
    <Tabs
      ariaLabel="Team"
      items={[
        {
          icon: <Users aria-hidden="true" className="size-3.5" />,
          label: "People",
          value: "people",
        },
        {
          icon: <GraduationCap aria-hidden="true" className="size-3.5" />,
          label: "Training",
          value: "training",
        },
      ]}
      onChange={(value) =>
        void navigate({
          replace: true,
          search: value === "training" ? { tab: "training" } : {},
          to: "/team",
        })
      }
      value={view}
      variant="segmented"
    />
  ) : null;

  return (
    <>
      <PageHeader className="mb-5" title={canManage ? "Team" : "Training"} />
      {view === "people" ? (
        <PeopleWorkspacePage tabs={tabs} />
      ) : (
        <TrainingWorkspacePage tabs={tabs} />
      )}
    </>
  );
}
